import React, { useState, useRef, useCallback, useMemo } from "react";
import { showToast } from "ui/components/common";
import clsx from "clsx";
interface RectangleSelectorProps {
  imgUrl: string;
  onCropImg?: (imgBlob: Blob) => void;
}

const RectangleSelector: React.FC<RectangleSelectorProps> = ({
  imgUrl,
  onCropImg,
}) => {
  const [imgLoaded, setImgLoaded] = useState(false);
  const [isSelecting, setIsSelecting] = useState(false);
  const [points, setPoints] = useState<[number, number][]>([]);
  const [imageSize, setImageSize] = useState<{
    width: number;
    height: number;
  }>();
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const onMousePress = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!imageRef.current || !imgLoaded) return;

    const imageRect = imageRef.current.getBoundingClientRect();
    onCropImg?.(null);

    // Calculate the coordinates of the click position relative to the image
    const x = e.clientX - imageRect.left;
    const y = e.clientY - imageRect.top;
    setPoints([[x, y]]);

    setIsSelecting(true);
  };

  const onMouseUp = (e: React.MouseEvent) => {
    if (!imageRef.current || !imgLoaded) return;
    console.log("onMouseUp", isSelecting, points);
    if (!isSelecting || points.length === 0) return;

    const imageRect = imageRef.current.getBoundingClientRect();

    // Calculate the coordinates of the click position relative to the image
    const x = e.clientX - imageRect.left;
    const y = e.clientY - imageRect.top;
    const twoPoints = [points?.at(0), [x, y]] as [number, number][];
    setPoints(twoPoints);
    handleCropFinish(twoPoints);

    setIsSelecting(false);
  };

  const onMouserMove = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isSelecting || points.length === 0) return;

    const imageRect = imageRef.current.getBoundingClientRect();

    const x = e.clientX - imageRect.left;
    const y = e.clientY - imageRect.top;
    setPoints([points?.at(0), [x, y]]);
  };

  const handleCropFinish = (points: [number, number][]) => {
    const canvas = canvasRef.current;
    const imageRect = imageRef.current.getBoundingClientRect();
    const ctx = canvas.getContext("2d");
    const img = imageRef.current;
    const xScale = imageSize.width / imageRect.width;
    const yScale = imageSize.height / imageRect.height;
    const x = Math.min(points[0][0], points[1][0]) * xScale;
    const y = Math.min(points[0][1], points[1][1]) * yScale;
    const width = Math.abs(points[0][0] - points[1][0]) * xScale;
    const height = Math.abs(points[0][1] - points[1][1]) * yScale;
    // filter mouse click event
    if (width < 1 || height < 1) {
      return;
    }
    if (width < 50 || height < 50) {
      showToast(
        1,
        "The width and height cannot be less than 50 pixels. Please select again.",
      );
      setPoints([]);
      return;
    }
    canvas.width = width;
    canvas.height = height;
    ctx.drawImage(img, x, y, width, height, 0, 0, width, height);
    canvas.toBlob((blob) => {
      onCropImg?.(blob);
    });
  };

  const maskSvg = useMemo(() => {
    let width, height, left, top;
    if (points.length === 2) {
      width = Math.abs(points[0][0] - points[1][0]);
      height = Math.abs(points[0][1] - points[1][1]);
      left = Math.min(points[0][0], points[1][0]);
      top = Math.min(points[0][1], points[1][1]);
    }
    return (
      <svg
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
        }}
      >
        {/* 定义遮罩：全屏半透明 + 中间透明矩形 */}
        <mask id="mask">
          <rect x="0" y="0" width="100%" height="100%" fill="white" />
          {points.length === 2 && (
            <rect
              x={left}
              y={top}
              width={width}
              height={height}
              fill="black"
              stroke="white"
              strokeWidth="4"
              strokeDasharray="5,5"
              rx="20"
              ry="20"
            />
          )}
        </mask>

        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.5)"
          mask="url(#mask)"
        />
      </svg>
    );
  }, [points]);

  return (
    <div
      ref={containerRef}
      className={clsx(
        "relative flex items-center justify-center rounded-lg overflow-hidden h-full w-full",
        imgLoaded ? "cursor-crosshair" : "cursor-default",
      )}
    >
      <canvas ref={canvasRef} style={{ display: "none" }} />
      <img
        ref={imageRef}
        src={imgUrl}
        className="max-w-full max-h-full object-contain w-full h-full"
        alt="selection target"
        crossOrigin="anonymous"
        onMouseMove={onMouserMove}
        onMouseDown={onMousePress}
        onMouseUp={onMouseUp}
        onLoad={(e) => {
          const img = e.target as HTMLImageElement;
          setImageSize({ width: img.naturalWidth, height: img.naturalHeight });
          setImgLoaded(true);
        }}
      />
      {maskSvg}
    </div>
  );
};

export default RectangleSelector;
